import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableProps,
  Space,
  ConfigProvider,
  theme,
  DatePicker,
  Button as AntdButton,
  Tag,
} from "antd";
import { Input } from "@/components/ui/inputForm";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { getVendas } from "@/services/venda";
import { errorHandler } from "@/utils/errorhandler";
import { usePermissions } from "@/hooks/usePermissions";

const MENU_ID_VENDA_PRODUTO = 33;

const { RangePicker } = DatePicker;

export default function ConsultaVenda() {
  const [vendas, setVendas] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [periodo, setPeriodo] = useState<[Dayjs, Dayjs] | null>(null);
  const navigate = useNavigate();
  const { canInsert, canEdit } = usePermissions();

  const handleGetVendas = useCallback(async () => {
    try {
      const response = await getVendas(
        periodo
          ? {
              dataInicio: periodo[0].format("YYYY-MM-DD"),
              dataFim: periodo[1].format("YYYY-MM-DD"),
            }
          : undefined
      );
      if (response.status === 200) {
        setVendas(response.data);
      }
    } catch (error) {
      errorHandler(error);
    }
  }, [periodo]);

  useEffect(() => {
    handleGetVendas();
  }, [handleGetVendas]);

  const handleAdd = useCallback(() => {
    navigate("/venda-produto/cadastro");
  }, [navigate]);

  const filteredVendas = vendas.filter((v) => {
    const termo = searchTerm.toLowerCase();
    return (
      !termo ||
      v.alunoNome?.toLowerCase().includes(termo) ||
      String(v.matricula ?? "").includes(termo)
    );
  });

  const columns: TableProps<any>["columns"] = [
    {
      title: "Data",
      dataIndex: "dataVenda",
      key: "dataVenda",
      render: (value) => (value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-"),
    },
    {
      title: "Aluno",
      dataIndex: "alunoNome",
      key: "alunoNome",
      render: (value, record) => (record.matricula ? `${value} (${record.matricula})` : value),
    },
    {
      title: "Produtos",
      dataIndex: "produtos",
      key: "produtos",
      render: (produtos: string[]) => (
        <Space size={[0, 4]} wrap>
          {(produtos || []).map((nome, index) => (
            <Tag key={`${nome}-${index}`}>{nome}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Forma de Pagamento",
      key: "formaPagamento",
      // A venda pode ser parcelada em mais de uma forma; mostra todas (sem repetir)
      // e o total de parcelas quando houver parcelamento.
      render: (_, record) => {
        const formas: string[] = Array.from(new Set(record.formasPagamento || []));
        const resumo = formas.length > 0 ? formas.join(", ") : record.formaPagamento;
        if (!resumo) return "-";
        return record.quantidadeParcelas > 1 ? `${resumo} (${record.quantidadeParcelas}x)` : resumo;
      },
    },
    {
      title: "Valor Total",
      dataIndex: "valorTotal",
      key: "valorTotal",
      render: (value) =>
        Number(value || 0).toLocaleString("pt-br", { style: "currency", currency: "BRL" }),
    },
    {
      title: "Registrada por",
      dataIndex: "usuarioNome",
      key: "usuarioNome",
      render: (value) => value || "-",
    },
    {
      title: "Ação",
      key: "acao",
      render: (_, record) => (
        <Space size="middle">
          {canEdit(MENU_ID_VENDA_PRODUTO) && (
            <AntdButton
              type="text"
              icon={<EditOutlined />}
              title="Abrir venda"
              onClick={() => navigate(`/venda-produto/${record.id}`)}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Vendas de Produtos</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-8 items-end mb-8">
          <Input
            type="text"
            placeholder="Pesquisar por aluno ou matrícula"
            className="w-80"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <RangePicker
            format="DD/MM/YYYY"
            aria-label="Período da venda"
            onChange={(datas) =>
              setPeriodo(datas && datas[0] && datas[1] ? [datas[0], datas[1]] : null)
            }
          />
          {canInsert(MENU_ID_VENDA_PRODUTO) && (
            <AntdButton className="flex gap-2" onClick={handleAdd}>
              Nova venda
              <PlusOutlined />
            </AntdButton>
          )}
        </div>
        <div className="grid">
          <div>
            <Table columns={columns} dataSource={filteredVendas} rowKey="id" />
          </div>
        </div>
      </ConfigProvider>
    </>
  );
}
